import { useMemo, useRef, useState } from 'react';
import { CLIENT_IMPORT_ROW_CAP, type ImportClientsResult } from '@salonomy/types';
import { Button, Modal, Pill } from '@salonomy/ui';
import { useImportClients } from '../../data/clients.js';
import { useToast } from '../../lib/toast.js';
import { parseCsv } from '../../lib/csvParse.js';
import { downloadCsv } from '../../lib/csv.js';
import { mapCsv, type MappedRow, type ParsedImport } from './import-map.js';

/**
 * WS-14 — bulk client CSV import. The whole flow is CLIENT-SIDE up to submit: the file is read as text,
 * parsed (lib/csvParse), header-mapped (import-map), PREVIEWED with advisory flags, then POSTed as a JSON
 * array. The server FORCES marketing_consent OFF for every imported client (GDPR — bulk consent can never
 * be imported without proof), so the modal states that prominently. On submit we render the SERVER's
 * honest counts verbatim (created / duplicates / invalid-with-reasons) — never a client guess.
 */
const PREVIEW_LIMIT = 10;

const TEMPLATE_HEADERS = ['First name', 'Last name', 'Email', 'Phone', 'Notes'];
const TEMPLATE_SAMPLE = [
  'Ada',
  'Lovelace',
  'ada@example.com',
  '+44 7700 900123',
  'Prefers mornings',
];

export function ImportClientsModal({ onClose }: { onClose: () => void }) {
  const importer = useImportClients();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedImport | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportClientsResult | null>(null);
  const [busy, setBusy] = useState(false);

  const total = parsed?.rows.length ?? 0;
  const overCap = total > CLIENT_IMPORT_ROW_CAP;
  const flags = useMemo(() => summarise(parsed?.rows ?? []), [parsed]);

  async function onFile(file: File) {
    setResult(null);
    setParseError(null);
    setFileName(file.name);
    try {
      const text = await file.text();
      const grid = parseCsv(text);
      const mapped = mapCsv(grid);
      if (mapped.rows.length === 0) {
        setParsed(null);
        setParseError('No data rows were found in that file.');
        return;
      }
      setParsed(mapped);
    } catch {
      setParsed(null);
      setParseError('That file could not be read as CSV.');
    }
  }

  async function submit() {
    if (!parsed || overCap || total === 0) return;
    setBusy(true);
    try {
      const res = await importer.mutateAsync(parsed.rows.map((r) => r.row));
      setResult(res);
      // Honest, server-derived summary — only claim success if rows were actually created.
      if (res.created > 0)
        toast.success(`Imported ${res.created} client${res.created === 1 ? '' : 's'}`);
      else toast.toast('No new clients were imported', 'info');
    } catch {
      toast.error('The import could not be completed.');
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setParsed(null);
    setParseError(null);
    setResult(null);
    setFileName(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Import clients from CSV"
      width={560}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {result ? 'Done' : 'Cancel'}
          </Button>
          {result ? (
            <Button variant="outline" onClick={reset}>
              Import another file
            </Button>
          ) : (
            <Button onClick={submit} disabled={busy || !parsed || total === 0 || overCap}>
              {busy ? 'Importing…' : `Import ${total || ''} client${total === 1 ? '' : 's'}`.trim()}
            </Button>
          )}
        </>
      }
    >
      {result ? (
        <ResultSummary result={result} />
      ) : (
        <>
          <p className="muted" style={{ fontSize: 13, marginTop: 0, lineHeight: 1.5 }}>
            Upload a CSV exported from your old system. We read it in your browser — nothing is sent
            until you confirm the preview below.
          </p>

          <div style={{ margin: '12px 0' }}>
            <label
              htmlFor="import-csv-file"
              className="slm-stat__label"
              style={{ display: 'block', marginBottom: 6 }}
            >
              CSV file
            </label>
            <input
              id="import-csv-file"
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              aria-label="CSV file to import"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onFile(f);
              }}
            />
            {fileName ? (
              <div className="muted" style={{ fontSize: 12.5, marginTop: 6 }}>
                {fileName}
              </div>
            ) : null}
          </div>

          <div
            style={{
              background: 'var(--slm-soft, #F4F1FB)',
              border: '1px solid var(--slm-border, #E3E6ED)',
              borderRadius: 10,
              padding: '10px 12px',
              fontSize: 12.5,
              lineHeight: 1.5,
              marginBottom: 12,
            }}
          >
            <strong>Marketing consent stays OFF.</strong> Every imported client is created with
            marketing consent switched off (UK GDPR — consent can’t be bulk-imported without proof).
            Turn it on per client once you hold their consent.
          </div>

          <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
            Expected columns: <strong>First name</strong> (required), Last name, Email, Phone,
            Notes. A single name column is used as the first name.{' '}
            <button
              type="button"
              className="linklike"
              onClick={() =>
                downloadCsv('salonomy-client-import-template.csv', TEMPLATE_HEADERS, [
                  TEMPLATE_SAMPLE,
                ])
              }
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: 'var(--slm-accent, #6D4AFF)',
                cursor: 'pointer',
                font: 'inherit',
                textDecoration: 'underline',
              }}
            >
              Download a template
            </button>
            .
          </p>

          {parseError ? (
            <div role="alert" className="slm-field__error" style={{ marginTop: 8 }}>
              {parseError}
            </div>
          ) : null}

          {parsed ? (
            <PreviewTable parsed={parsed} total={total} flags={flags} overCap={overCap} />
          ) : null}
        </>
      )}
    </Modal>
  );
}

function summarise(rows: MappedRow[]): { missingFirstName: number; badEmail: number } {
  let missingFirstName = 0;
  let badEmail = 0;
  for (const r of rows) {
    if (r.missingFirstName) missingFirstName++;
    if (r.badEmail) badEmail++;
  }
  return { missingFirstName, badEmail };
}

function PreviewTable({
  parsed,
  total,
  flags,
  overCap,
}: {
  parsed: ParsedImport;
  total: number;
  flags: { missingFirstName: number; badEmail: number };
  overCap: boolean;
}) {
  const preview = parsed.rows.slice(0, PREVIEW_LIMIT);
  return (
    <div style={{ marginTop: 14 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}
      >
        <strong style={{ fontSize: 13.5 }}>Preview</strong>
        <Pill tone="neutral">{total} rows</Pill>
        {!parsed.hasHeader ? (
          <Pill tone="soft">No header detected — first column used as name</Pill>
        ) : null}
        {flags.missingFirstName > 0 ? (
          <Pill tone="danger">{flags.missingFirstName} missing first name</Pill>
        ) : null}
        {flags.badEmail > 0 ? <Pill tone="soft">{flags.badEmail} questionable email</Pill> : null}
      </div>

      {overCap ? (
        <div role="alert" className="slm-field__error" style={{ marginBottom: 8 }}>
          This file has {total} rows — over the {CLIENT_IMPORT_ROW_CAP}-row limit per import. Split
          it into files of {CLIENT_IMPORT_ROW_CAP} rows or fewer and import each in turn.
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table className="slm-table" style={{ fontSize: 12.5, width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>First name</th>
              <th style={{ textAlign: 'left' }}>Last name</th>
              <th style={{ textAlign: 'left' }}>Email</th>
              <th style={{ textAlign: 'left' }}>Phone</th>
            </tr>
          </thead>
          <tbody>
            {preview.map((r, i) => (
              <tr key={i}>
                <td
                  style={r.missingFirstName ? { color: 'var(--slm-danger, #C0362C)' } : undefined}
                >
                  {r.row.firstName || '—'}
                </td>
                <td>{r.row.lastName ?? ''}</td>
                <td style={r.badEmail ? { color: 'var(--slm-danger, #C0362C)' } : undefined}>
                  {r.row.email ?? ''}
                </td>
                <td>{r.row.phone ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > PREVIEW_LIMIT ? (
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          Showing the first {PREVIEW_LIMIT} of {total} rows.
        </div>
      ) : null}
    </div>
  );
}

function ResultSummary({ result }: { result: ImportClientsResult }) {
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <Pill tone="success">{result.created} created</Pill>
        <Pill tone="neutral">
          {result.duplicates} duplicate{result.duplicates === 1 ? '' : 's'} skipped
        </Pill>
        {result.invalid.length > 0 ? (
          <Pill tone="danger">{result.invalid.length} invalid</Pill>
        ) : null}
        <Pill tone="soft">{result.total} submitted</Pill>
      </div>

      <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
        Duplicates are rows whose email already existed in your book (or appeared earlier in this
        file) — they were left untouched. Re-running the same file is safe.
      </p>

      {result.invalid.length > 0 ? (
        <div style={{ marginTop: 8 }}>
          <strong style={{ fontSize: 13 }}>Rows skipped as invalid</strong>
          <ul style={{ fontSize: 12.5, margin: '6px 0 0', paddingLeft: 18, lineHeight: 1.6 }}>
            {result.invalid.slice(0, 50).map((inv) => (
              <li key={inv.row}>
                Row {inv.row + 1}: {inv.reason}
              </li>
            ))}
          </ul>
          {result.invalid.length > 50 ? (
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              …and {result.invalid.length - 50} more.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
